import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chat } from "./ui/Chat";
import { ChatMessage } from "./ui/types";
import { createChat, sendMessage, ChatMessageResponse } from "../../services/copilotService";
import { useCopilotContext } from "../../contexts/CopilotContext";
import { useAlerts } from "../../contexts/AlertsContext";
import { useToast } from "@/hooks/use-toast";

const CHAT_ID_STORAGE_KEY = "poc-copilot-chat-id";

interface PocCopilotChatProps {
  systemMessage?: string;
  title?: string;
  placeholder?: string;
  disabled?: boolean;
}

function mapToChatMessage(message: ChatMessageResponse): ChatMessage {
  return { 
    id: message.chatMessageId, 
    text: message.message, 
    authorRole: message.authorRole, 
    timestamp: new Date(message.createdAt), 
  };
}


function PocCopilotChat({ systemMessage, title = "Copilot", placeholder = "Ask anything about this page...", disabled }: PocCopilotChatProps): JSX.Element {
  const [isInitializing, setIsInitializing] = useState(true);
  const [currentChatId, setCurrentChatId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { alerts } = useAlerts();
  const { pendingMessage, setPendingMessage } = useCopilotContext();
  const { toast } = useToast();

  const handleCreateChat = useCallback(async (): Promise<string> => {
    try {
      const response = await createChat(systemMessage);
      sessionStorage.setItem(CHAT_ID_STORAGE_KEY, response.chatId);
      setCurrentChatId(response.chatId);
      setMessages([]);
      return response.chatId;
    } catch (error) {
      console.error("Failed to create chat:", error);
      throw error;
    }
  }, [systemMessage]);

  // Track if we've created a chat with the current systemMessage
  const [systemMessageApplied, setSystemMessageApplied] = useState<string | undefined>(undefined);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        // Always start fresh — system prompt contains graphData which may change on reload
        sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);

        if (systemMessage) {
          await handleCreateChat();
          setSystemMessageApplied(systemMessage);
        }
      } finally {
        setIsInitializing(false);
      }
    };
    initializeChat();
  }, [handleCreateChat, systemMessage]);

  // Create new chat when systemMessage changes (e.g., when graphData loads)
  useEffect(() => {
    if (systemMessage && systemMessage !== systemMessageApplied && !isInitializing) {
      handleCreateChat().then(() => {
        setSystemMessageApplied(systemMessage);
      });
    }
  }, [systemMessage, systemMessageApplied, isInitializing, handleCreateChat]);

  useEffect(() => {
    const handleAlertSent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.response) {
        const msg = mapToChatMessage(detail.response);
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
      if (detail?.chatId && !currentChatId) {
        setCurrentChatId(detail.chatId);
      }
    };
    window.addEventListener("copilot-alert-sent", handleAlertSent);
    return () => window.removeEventListener("copilot-alert-sent", handleAlertSent);
  }, [currentChatId]);

  const filteredMessages = useMemo(
    () => messages.filter((m) => !/^\s*Alert Received/i.test(m.text)),
    [messages]
  );

  const handleSendMessage = useCallback(async (text: string) => {
    setIsLoading(true);
    try {
      let chatId = currentChatId;
      if (!chatId) {
        chatId = await handleCreateChat();
      }

      // Optimistic update for user message
      const tempUserMsg: ChatMessage = {
        id: Date.now().toString(),
        text,
        authorRole: "User",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, tempUserMsg]);

      const activeAlerts = alerts.map((alert) => ({
        id: alert.id,
        type: alert.type,
        title: alert.title,
        nodeName: alert.nodeName,
        health: alert.health,
        timestamp: alert.timestamp.toISOString(),
      }));

      const activePageContext = {
        alerts: activeAlerts,
      };

      const response = await sendMessage(chatId, text, activePageContext);
      const botMsg = mapToChatMessage(response);
      
      setMessages(prev => [...prev, botMsg]);
      
    } catch (error) {
      console.error("Failed to send message:", error);
      toast({
        title: "Copilot error",
        description: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentChatId, handleCreateChat, alerts, toast]);

  const handleClearChat = useCallback(() => {
    handleCreateChat();
  }, [handleCreateChat]);

  // Handle pending messages from other components (e.g., AlertsPanel Analyze button)
  useEffect(() => {
    if (pendingMessage && !isInitializing && !isLoading) {
      handleSendMessage(pendingMessage);
      setPendingMessage(null);
    }
  }, [pendingMessage, isInitializing, isLoading, handleSendMessage, setPendingMessage]);

  return (
    <div style={{ height: '100%' }}>
      <Chat
        messages={filteredMessages}
        isLoading={isInitializing || isLoading}
        onSendMessage={handleSendMessage}
        onClearChat={handleClearChat}
        placeholder={placeholder}
        title={title}
        disabled={disabled}
      />
    </div>
  );
}

export { PocCopilotChat };
